/**
 * components/StockWarningModal.js
 * "Peringatan Stok" — a speed bump, not a wall (Phase T1e, PRD invariant 2: negative stock is
 * always legal). Shown right before a transaction is saved when any line both ends up negative
 * AND makes the item's real current stock worse (see `TransactionFormModal.js`'s own `submit()`
 * for the exact condition and why "the result is merely negative" isn't enough on its own —
 * that broader check used to also fire on a PEMBELIAN line that only IMPROVES an
 * already-negative balance, which is never something to warn about). Names each affected line
 * (qty, item name, available stock, and that stock will go negative). "Tetap Lanjutkan" proceeds
 * with the save exactly as if this modal never existed; "Batal & Koreksi" (or Esc, or clicking
 * outside) just closes it and returns to the form with every field exactly as the user left it —
 * nothing is reset.
 *
 * **`txnType`-aware wording (bug fix)** — this used to hardcode "Menjual" regardless of
 * `txnType`, which read backwards on a PEMBELIAN (a purchase, described as if it were a sale)
 * and said nothing distinct for a TRANSFER either. `ACTION_LABEL` below picks the right verb per
 * type; PEMBELIAN's own wording is phrased as a CORRECTION deliberately — a create-mode
 * PEMBELIAN line can never reach this modal at all (it only ever adds, so `after` can never be
 * less than the item's real current stock), so the only way a PEMBELIAN line ever appears here
 * is by editing an existing purchase down to a smaller qty.
 *
 * Follows the app's standard modal-shell + useModalEsc convention (mirrors
 * DeleteConfirmModal's shape) — not a typed-confirm gate, since this never blocks anything.
 */
import React from "react";
import { fmtQty } from "../utils/idGenerators";
import { useModalEsc, backdropClose } from "../utils/modalStack";

const ACTION_LABEL = {
  PENJUALAN: "Menjual",
  PEMBELIAN: "Mengoreksi pembelian menjadi",
  TRANSFER: "Mentransfer",
};

export default function StockWarningModal({ lines = [], txnType, onProceed, onCancel }) {
  useModalEsc(onCancel);
  const actionLabel = ACTION_LABEL[txnType] || "Memproses";

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onCancel)}>
      <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Peringatan Stok</h2>
        <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
          {lines.length === 1
            ? "Baris berikut akan membuat stok menjadi minus:"
            : "Baris-baris berikut akan membuat stok menjadi minus:"}
          <ul style={{ margin: "10px 0 0", paddingLeft: 18 }}>
            {lines.map((l, i) => (
              <li key={i} style={{ marginBottom: "var(--gap-sm)" }}>
                {actionLabel} <b>{fmtQty(l.qty)} PCS {l.name || l.internalCode}</b> — stok tersedia{" "}
                <b>{fmtQty(l.before)}</b>, akan menjadi{" "}
                <b style={{ color: "var(--red)" }}>{fmtQty(l.after)} (minus)</b>.
              </li>
            ))}
          </ul>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel}>Batal &amp; Koreksi</button>
          <button type="button" className="btn btn-primary" onClick={onProceed}>Tetap Lanjutkan</button>
        </div>
      </div>
    </div>
  );
}
